export type DatePreset =
  | "today"
  | "this_week"
  | "this_month"
  | "last_quarter"
  | "last_6_months"
  | "this_year";

export const PRESET_LABELS: Record<DatePreset, string> = {
  today: "Today",
  this_week: "This Week",
  this_month: "This Month",
  last_quarter: "Last Quarter",
  last_6_months: "Last 6 Months",
  this_year: "This Year",
};

export const ALL_PRESETS: DatePreset[] = [
  "today",
  "this_week",
  "this_month",
  "last_quarter",
  "last_6_months",
  "this_year",
];

export function getPresetDateRange(preset: DatePreset): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  let start: Date;
  let end: Date = new Date(now);
  end.setHours(23, 59, 59, 999);

  switch (preset) {
    case "today":
      start = new Date(now);
      start.setHours(0, 0, 0, 0);
      break;

    case "this_week": {
      start = new Date(now);
      const day = now.getDay(); // 0 = Sunday
      start.setDate(now.getDate() - day);
      start.setHours(0, 0, 0, 0);
      break;
    }

    case "this_month":
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      break;

    case "last_quarter": {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      if (currentQuarter === 0) {
        // Previous year Q4
        start = new Date(now.getFullYear() - 1, 9, 1);
        end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      } else {
        start = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
        end = new Date(
          now.getFullYear(),
          currentQuarter * 3,
          0,
          23,
          59,
          59,
          999
        );
      }
      break;
    }

    case "last_6_months":
      start = new Date(now);
      start.setMonth(now.getMonth() - 6);
      start.setHours(0, 0, 0, 0);
      break;

    case "this_year":
      start = new Date(now.getFullYear(), 0, 1);
      start.setHours(0, 0, 0, 0);
      break;
  }

  return {
    startDate: start!.toISOString(),
    endDate: end.toISOString(),
  };
}

/**
 * Returns a human-readable "since …" label for the percentage-change row
 * on each stat card, based on the active date filter preset.
 */
export function getComparisonLabel(preset: DatePreset | "custom" | null): string {
  switch (preset) {
    case null:          return "since last week";
    case "today":       return "since yesterday";
    case "this_week":   return "since last week";
    case "this_month":  return "since last month";
    case "last_quarter":  return "since prior quarter";
    case "last_6_months": return "since prior 6 months";
    case "this_year":   return "since last year";
    case "custom":      return "since prior period";
    default:            return "since prior period";
  }
}

/**
 * Computes the semantically correct prior comparison period for each preset,
 * so percentage changes are always "apples-to-apples":
 *
 *   today         → yesterday (same 24 h window)
 *   this_week     → same days last week (shift back 7 days)
 *   this_month    → full previous calendar month
 *   last_quarter  → the quarter before last quarter
 *   last_6_months → the 6 calendar months before the selected start
 *   this_year     → same Jan 1–today window one year earlier
 *   custom        → equal-length window immediately before the selected range
 */
export function computePriorDateRange(
  preset: DatePreset | "custom",
  currentStart: Date,
  currentEnd: Date
): { priorStartDate: string; priorEndDate: string } {
  let priorStart: Date;
  let priorEnd: Date;

  switch (preset) {
    case "today":
      // Yesterday, same hours
      priorStart = new Date(currentStart);
      priorStart.setDate(currentStart.getDate() - 1);
      priorEnd = new Date(currentEnd);
      priorEnd.setDate(currentEnd.getDate() - 1);
      break;

    case "this_week":
      // Same days of the previous week
      priorStart = new Date(currentStart);
      priorStart.setDate(currentStart.getDate() - 7);
      priorEnd = new Date(currentEnd);
      priorEnd.setDate(currentEnd.getDate() - 7);
      break;

    case "this_month": {
      // Full previous calendar month
      const prevMonth = currentStart.getMonth() - 1;
      const year =
        prevMonth < 0
          ? currentStart.getFullYear() - 1
          : currentStart.getFullYear();
      const month = prevMonth < 0 ? 11 : prevMonth;
      priorStart = new Date(year, month, 1, 0, 0, 0, 0);
      // Last day of that month
      priorEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);
      break;
    }

    case "last_quarter":
      // Quarter before last quarter: shift the current range back 3 months
      priorStart = new Date(currentStart);
      priorStart.setMonth(currentStart.getMonth() - 3);
      priorEnd = new Date(currentStart);
      priorEnd.setDate(currentStart.getDate() - 1);
      priorEnd.setHours(23, 59, 59, 999);
      break;

    case "last_6_months":
      // The 6 calendar months immediately before the selected start
      priorStart = new Date(currentStart);
      priorStart.setMonth(currentStart.getMonth() - 6);
      priorEnd = new Date(currentStart);
      priorEnd.setDate(currentStart.getDate() - 1);
      priorEnd.setHours(23, 59, 59, 999);
      break;

    case "this_year":
      // Same Jan 1 → same calendar day, one year prior
      priorStart = new Date(currentStart);
      priorStart.setFullYear(currentStart.getFullYear() - 1);
      priorEnd = new Date(currentEnd);
      priorEnd.setFullYear(currentEnd.getFullYear() - 1);
      break;

    case "custom":
    default: {
      // Equal-length window immediately before the selected range
      const durationMs = currentEnd.getTime() - currentStart.getTime();
      priorStart = new Date(currentStart.getTime() - durationMs);
      priorEnd = new Date(currentStart.getTime() - 1); // 1 ms before range start
      break;
    }
  }

  return {
    priorStartDate: priorStart.toISOString(),
    priorEndDate: priorEnd.toISOString(),
  };
}
